import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../shared/api';
import { canOpenModule } from '../shared/biModuleKeys';
import { SHELL_MODULE_REGISTRY, type ModuleKey } from '../shared/biModuleRegistry';
import { formatMoney, formatNumber } from '../shared/formatting';
import type { AdoPulsePayload, CashflowPayload, Filters, FoundationPayload, LoansPayload, SalesAnalyticsPayload, User } from '../types/biApp';
import './commandCenterModule.css';

type Locale = 'hr' | 'en';
type Task = { id: number; title: string; due_date: string | null; priority: string };
type TaskInbox = { overdue: Task[]; today: Task[]; upcoming: Task[]; no_date: Task[] };
type Project = { id: number; name: string; color: string; status: string; due_date: string | null; task_count: number; completed_count: number };
type Stock = { own_stock: Array<{ available_quantity: number; reserved_quantity: number }>; levels: Array<{ available_quantity: number; reserved_quantity: number }> };
type Turnover = { day: number; week: number; month: number };
type GapOverview = { book_eur?: { net_gap?: number }; fine_xau?: { net_gap?: number }; spot_eur?: { net_gap?: number } };

const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const pct = (value: number, max: number) => `${Math.max(2, Math.min(100, max > 0 ? value / max * 100 : 0))}%`;
const compactEur = (n: number, locale: Locale) => {
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
    return formatNumber(Math.round(n), locale);
};
const sign = (n: number) => n > 0 ? '+' : n < 0 ? '-' : '';
const tone = (n: number) => n > 0 ? 'pos' : n < 0 ? 'neg' : '';

// Sparkline SVG - inline mini chart
function Spark({ values, width = 80, height = 24, color = 'var(--cc-ink)' }: { values: number[]; width?: number; height?: number; color?: string }) {
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const pad = 2;
    const points = values.map((v, i) => `${pad + (i / Math.max(values.length - 1, 1)) * (width - pad * 2)},${pad + (1 - (v - min) / range) * (height - pad * 2)}`).join(' ');
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="cc-spark">
            <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

export function CommandCenterModule({ user, token, locale, foundation, cashflow, loans, filters, onOpenModule }: {
    user: User;
    token: string;
    locale: Locale;
    foundation: FoundationPayload;
    cashflow: CashflowPayload | null;
    loans: LoansPayload | null;
    filters: Filters;
    onOpenModule: (key: ModuleKey) => void;
}) {
    const t = (hr: string, en: string) => locale === 'en' ? en : hr;
    const [clock, setClock] = useState(new Date());
    const [tasks, setTasks] = useState<TaskInbox | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [stock, setStock] = useState<Stock | null>(null);
    const [turnover, setTurnover] = useState<Turnover | null>(null);
    const [pulse, setPulse] = useState<AdoPulsePayload | null>(null);
    const [gap, setGap] = useState<GapOverview | null>(null);

    // Clock tick every minute
    useEffect(() => {
        const timer = window.setInterval(() => setClock(new Date()), 60000);
        return () => window.clearInterval(timer);
    }, []);

    // Parallel data fetch
    useEffect(() => {
        void Promise.all([
            api<TaskInbox>('/tasks/my-tasks', token).catch(() => null),
            api<{ projects: Project[] }>('/tasks/projects', token).catch(() => ({ projects: [] })),
        ]).then(([taskData, projectData]) => {
            setTasks(taskData);
            setProjects(projectData.projects ?? []);
        });
    }, [token]);

    useEffect(() => {
        if (!canOpenModule(user, 'warehouse_stock')) return;
        void api<Stock>('/warehouse-stock/overview', token).then(setStock).catch(() => setStock(null));
    }, [token, user]);

    useEffect(() => {
        if (!canOpenModule(user, 'ado')) return;
        const now = new Date();
        const end = iso(now);
        const monday = new Date(now);
        monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
        const month = new Date(now.getFullYear(), now.getMonth(), 1);
        const companies = filters.companyIds.length ? `&company_ids=${filters.companyIds.join(',')}` : '';
        const read = (start: string) => api<SalesAnalyticsPayload>(`/ado?date_from=${start}&date_to=${end}&dataset_mode=invoice_view${companies}`, token)
            .then((value) => Number(value.overview.turnover_total_amount ?? value.overview.realized_amount ?? 0));
        void Promise.all([read(end), read(iso(monday)), read(iso(month))])
            .then(([day, week, monthTotal]) => setTurnover({ day, week, month: monthTotal }))
            .catch(() => setTurnover(null));
    }, [token, user, filters.companyIds]);

    // Pulse (ADO live metrics)
    useEffect(() => {
        if (!canOpenModule(user, 'ado')) return;
        const companies = filters.companyIds.length ? `?company_ids=${filters.companyIds.join(',')}` : '';
        void api<AdoPulsePayload>(`/ado/pulse${companies}`, token).then(setPulse).catch(() => setPulse(null));
    }, [token, user, filters.companyIds]);

    // GAP report
    useEffect(() => {
        if (!canOpenModule(user, 'warehouse_gold_coverage')) return;
        void api<GapOverview>('/gap-report/overview', token).then(setGap).catch(() => setGap(null));
    }, [token, user]);

    // Derived data
    const modules = useMemo(() => SHELL_MODULE_REGISTRY
        .filter((entry) => !entry.parentKey && !entry.hiddenFromNav && entry.key !== 'dashboard' && canOpenModule(user, entry.key))
        .sort((a, b) => a.code - b.code), [user]);
    const activeProjects = projects.filter((p) => !['completed', 'archived'].includes(p.status));
    const taskList = [...(tasks?.overdue ?? []), ...(tasks?.today ?? []), ...(tasks?.upcoming ?? [])].slice(0, 6);
    const overdueCount = tasks?.overdue?.length ?? 0;
    const todayCount = tasks?.today?.length ?? 0;

    const stockRows = stock?.own_stock?.length ? stock.own_stock : stock?.levels ?? [];
    const available = stockRows.reduce((s, r) => s + Number(r.available_quantity ?? 0), 0);
    const reserved = stockRows.reduce((s, r) => s + Number(r.reserved_quantity ?? 0), 0);

    const cash = Number(cashflow?.balances.consolidated.closing ?? 0);
    const cashMove = Number(cashflow?.balances.consolidated.change ?? 0);
    const debt = Math.abs(Number(loans?.balances.closing ?? 0));
    const treasury = cashflow?.treasury;
    const runway = treasury?.cash_runway_days;

    const decisionCount = overdueCount + activeProjects.filter((p) => p.due_date && new Date(p.due_date) < clock).length;
    const lateProjects = activeProjects.filter((p) => p.due_date && new Date(p.due_date) < clock).length;

    const maxTurnover = Math.max(turnover?.day ?? 0, turnover?.week ?? 0, turnover?.month ?? 0, 1);
    const firstName = (user.name ?? '').split(' ')[0];

    // Pulse KPIs
    const kpi = pulse?.kpis;
    const trendValues = (pulse?.trend_daily ?? []).map((d) => d.promet_eur);
    const buybackTrend = (pulse?.buyback_trend ?? []).map((d) => d.buyback_eur);
    const openRisk = kpi?.open_risk_eur ?? 0;
    const openRiskCount = kpi?.open_risk_count ?? 0;
    const conversion = kpi?.conversion_pct ?? 0;

    // Cashflow graph sparkline
    const cashGraph = (cashflow?.graph ?? []).slice(-30).map((p: { y?: number; value?: number; net?: number }) => Number(p.y ?? p.value ?? p.net ?? 0));

    // Alerts
    const alerts: Array<{ level: 'critical' | 'warn'; text: string; module: ModuleKey }> = [];
    if (overdueCount > 0) alerts.push({ level: overdueCount >= 5 ? 'critical' : 'warn', text: t(`${overdueCount} zakasnjel${overdueCount === 1 ? 'i' : 'ih'} zadatak/a`, `${overdueCount} overdue task${overdueCount === 1 ? '' : 's'}`), module: 'team_tasks' });
    if (treasury?.alerts.overdue_count && treasury.alerts.overdue_count > 0) alerts.push({ level: 'warn', text: t(`${treasury.alerts.overdue_count} dospjelih obveza (${compactEur(treasury.alerts.overdue_amount ?? 0, locale)} EUR)`, `${treasury.alerts.overdue_count} overdue obligations (${compactEur(treasury.alerts.overdue_amount ?? 0, locale)} EUR)`), module: 'cashflow' });
    if (runway !== null && runway !== undefined && runway < 30) alerts.push({ level: runway < 14 ? 'critical' : 'warn', text: t(`Runway: ${runway} dana`, `Runway: ${runway} days`), module: 'cashflow' });
    if (lateProjects > 0) alerts.push({ level: 'warn', text: t(`${lateProjects} projekt${lateProjects === 1 ? '' : 'a'} kasni`, `${lateProjects} late project${lateProjects === 1 ? '' : 's'}`), module: 'team_tasks' });
    if ((pulse?.alerts ?? []).some((a) => a.severity === 'critical')) alerts.push({ level: 'critical', text: t('Prodaja: kriticni alert', 'Sales: critical alert'), module: 'ado' });
    const hasCritical = alerts.some((a) => a.level === 'critical');

    const nav = useCallback((key: ModuleKey) => () => onOpenModule(key), [onOpenModule]);

    return (
        <section className="cc" aria-label={t('Upravljacki centar', 'Command center')}>
            {/* === ALERT BANNER === */}
            {alerts.length > 0 && (
                <div className={`cc-alerts ${hasCritical ? 'is-critical' : ''}`}>
                    {alerts.map((a, i) => (
                        <button type="button" key={i} className={`cc-alert cc-alert-${a.level}`} onClick={nav(a.module)}>
                            <span className="cc-alert-dot" />{a.text}
                        </button>
                    ))}
                </div>
            )}

            {/* === EXECUTIVE BRIEF === */}
            <div className="cc-brief">
                <div>
                    <p className="cc-label">{t('UPRAVLJACKI SAZETAK', 'EXECUTIVE BRIEF')}</p>
                    <h1>{t('Dobar dan', 'Good day')}{firstName ? `, ${firstName}` : ''}.</h1>
                    <h2>{decisionCount > 0
                        ? t(`${decisionCount} stavki danas trazi odluku.`, `${decisionCount} items need a decision today.`)
                        : t('Poslovanje je mirno. Nema eskalacija.', 'Operations are calm. No escalations.')}</h2>
                </div>
                <div className="cc-brief-right">
                    <button type="button" className="cc-decision" onClick={nav('team_tasks')}>
                        <small>{t('RED ODLUKA', 'DECISION QUEUE')}</small>
                        <strong>{decisionCount}</strong>
                        <span>{t('otvori', 'open')}</span>
                    </button>
                    <div className="cc-clock">
                        <span className="cc-dot" />{clock.toLocaleTimeString(locale === 'en' ? 'en-GB' : 'hr-HR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </div>

            {/* === SIGNAL STRIP - 8 KPIs === */}
            <div className="cc-signals">
                <button type="button" className="cc-sig" onClick={nav('cashflow')}>
                    <span className="cc-sig-label">{t('LIKVIDNOST', 'LIQUIDITY')}</span>
                    <strong className="cc-sig-value">{compactEur(cash, locale)}</strong>
                    <span className={`cc-sig-delta ${tone(cashMove)}`}>{sign(cashMove)}{compactEur(Math.abs(cashMove), locale)}</span>
                </button>
                <button type="button" className="cc-sig" onClick={nav('ado')}>
                    <span className="cc-sig-label">{t('PROMET DANAS', 'REVENUE TODAY')}</span>
                    <strong className="cc-sig-value">{turnover ? compactEur(turnover.day, locale) : '...'}</strong>
                    {trendValues.length > 0 && <Spark values={trendValues} width={64} height={18} />}
                </button>
                <button type="button" className="cc-sig" onClick={nav('ado')}>
                    <span className="cc-sig-label">{t('PROMET MJESEC', 'REVENUE MTD')}</span>
                    <strong className="cc-sig-value">{turnover ? compactEur(turnover.month, locale) : '...'}</strong>
                    {kpi && <span className={`cc-sig-delta ${tone(kpi.promet_yoy_pct)}`}>{kpi.promet_yoy_pct > 0 ? '+' : ''}{kpi.promet_yoy_pct.toFixed(0)}% YoY</span>}
                </button>
                <button type="button" className="cc-sig" onClick={nav('loans')}>
                    <span className="cc-sig-label">{t('IC POZAJMICE', 'IC LOANS')}</span>
                    <strong className="cc-sig-value">{compactEur(debt, locale)}</strong>
                </button>
                <button type="button" className="cc-sig" onClick={nav('ado')}>
                    <span className="cc-sig-label">{t('KONVERZIJA', 'CONVERSION')}</span>
                    <strong className="cc-sig-value">{conversion.toFixed(0)}%</strong>
                </button>
                <button type="button" className="cc-sig" onClick={nav('ado')}>
                    <span className="cc-sig-label">{t('OTVORENI RIZIK', 'OPEN RISK')}</span>
                    <strong className="cc-sig-value">{compactEur(openRisk, locale)}</strong>
                    <span className="cc-sig-sub">{openRiskCount} {t('ponuda', 'offers')}</span>
                </button>
                <button type="button" className="cc-sig" onClick={nav('warehouse_stock')}>
                    <span className="cc-sig-label">{t('ZALIHE', 'INVENTORY')}</span>
                    <strong className="cc-sig-value">{stock ? formatNumber(available, locale) : '...'}</strong>
                    <span className="cc-sig-sub">{formatNumber(reserved, locale)} {t('rez.', 'rsv.')}</span>
                </button>
                <button type="button" className="cc-sig" onClick={nav('cashflow')}>
                    <span className="cc-sig-label">RUNWAY</span>
                    <strong className={`cc-sig-value ${runway !== null && runway !== undefined && runway < 30 ? 'is-warn' : ''}`}>{runway != null ? `${runway}d` : '-'}</strong>
                </button>
            </div>

            {/* === MAIN GRID === */}
            <div className="cc-grid">
                {/* --- Revenue --- */}
                <article className="cc-card cc-revenue" onClick={nav('ado')} role="button" tabIndex={0}>
                    <div className="cc-card-head"><span>01</span><h3>{t('Ritam prihoda', 'Revenue rhythm')}</h3></div>
                    <div className="cc-bars">
                        {([['day', t('Danas', 'Today')], ['week', t('Tjedan', 'Week')], ['month', t('Mjesec', 'Month')]] as const).map(([key, label]) => (
                            <div className="cc-bar-row" key={key}>
                                <div><span>{label}</span><strong>{turnover ? formatMoney(turnover[key], locale) : '...'}</strong></div>
                                <i><b style={{ width: pct(turnover?.[key] ?? 0, maxTurnover) }} /></i>
                            </div>
                        ))}
                    </div>
                    {trendValues.length > 2 && (
                        <div className="cc-card-chart">
                            <span className="cc-chart-label">{t('DNEVNI TREND', 'DAILY TREND')}</span>
                            <Spark values={trendValues} width={240} height={36} />
                        </div>
                    )}
                    {kpi && kpi.buyback_eur > 0 && (
                        <div className="cc-card-foot">
                            <span>{t('Otkup', 'Buyback')}</span>
                            <b>{formatMoney(kpi.buyback_eur, locale)}</b>
                            <em>{kpi.buyback_count} {t('tr.', 'tx.')}</em>
                        </div>
                    )}
                </article>

                {/* --- Liquidity --- */}
                <article className="cc-card cc-liquidity" onClick={nav('cashflow')} role="button" tabIndex={0}>
                    <div className="cc-card-head"><span>02</span><h3>{t('Financijska pozicija', 'Financial position')}</h3></div>
                    <div className="cc-big-number"><small>{t('RASPOLOZIVO', 'AVAILABLE')}</small><strong>{formatMoney(cash, locale)}</strong></div>
                    {cashGraph.length > 2 && (
                        <div className="cc-card-chart">
                            <span className="cc-chart-label">{t('30 DANA', '30 DAYS')}</span>
                            <Spark values={cashGraph} width={200} height={32} color={cashMove >= 0 ? '#4a7c3f' : '#a12820'} />
                        </div>
                    )}
                    <div className="cc-split">
                        <div><span>{t('Promjena', 'Movement')}</span><b className={cashMove < 0 ? 'is-negative' : ''}>{formatMoney(cashMove, locale)}</b></div>
                        <div><span>{t('IC pozajmice', 'IC loans')}</span><b>{formatMoney(debt, locale)}</b></div>
                    </div>
                    {treasury && (
                        <div className="cc-treasury">
                            <div><span>{t('Dospijeva 7d', 'Due 7d')}</span><b>{compactEur(treasury.due_7d ?? 0, locale)}</b></div>
                            <div><span>{t('Projekcija 30d', 'Proj. 30d')}</span><b className={tone((treasury.projection_30d?.net ?? 0))}>{compactEur(treasury.projection_30d?.net ?? 0, locale)}</b></div>
                        </div>
                    )}
                </article>

                {/* --- Inventory --- */}
                <article className="cc-card cc-stock" onClick={nav('warehouse_stock')} role="button" tabIndex={0}>
                    <div className="cc-card-head"><span>03</span><h3>{t('Zalihe', 'Inventory')}</h3></div>
                    <div className="cc-stock-orbit">
                        <div>
                            <strong>{stock ? formatNumber(available, locale) : '...'}</strong>
                            <span>{t('raspolozivo', 'available')}</span>
                        </div>
                        <i style={{ '--fill': `${available + reserved > 0 ? available / (available + reserved) * 360 : 0}deg` } as React.CSSProperties} />
                    </div>
                    <div className="cc-stock-foot">
                        <span>{formatNumber(reserved, locale)} {t('rezervirano', 'reserved')}</span>
                        <span>{stockRows.length} {t('artikala', 'items')}</span>
                    </div>
                    {gap && gap.book_eur && (
                        <div className="cc-gap">
                            <span>GAP</span>
                            <b className={tone(gap.book_eur.net_gap ?? 0)}>{compactEur(gap.book_eur.net_gap ?? 0, locale)} EUR</b>
                        </div>
                    )}
                </article>

                {/* --- Work in motion --- */}
                <article className="cc-card cc-work">
                    <div className="cc-card-head"><span>04</span><h3>{t('Rad koji se mice', 'Work in motion')}</h3><button type="button" onClick={nav('team_tasks')}>{t('Projekti', 'Projects')}</button></div>
                    <div className="cc-projects">
                        {activeProjects.slice(0, 4).map((project) => {
                            const p = project.task_count > 0 ? project.completed_count / project.task_count * 100 : 0;
                            return (
                                <button type="button" key={project.id} onClick={nav('team_tasks')}>
                                    <span style={{ background: project.color }} />
                                    <div>
                                        <strong>{project.name}</strong>
                                        <i><b style={{ width: `${p}%` }} /></i>
                                    </div>
                                    <em>{Math.round(p)}%</em>
                                </button>
                            );
                        })}
                        {activeProjects.length === 0 && <p className="cc-empty">{t('Nema aktivnih projekata.', 'No active projects.')}</p>}
                    </div>
                </article>

                {/* --- Task queue --- */}
                <article className="cc-card cc-next">
                    <div className="cc-card-head">
                        <span>05</span>
                        <h3>{t('Radni red', 'Work queue')}</h3>
                        <button type="button" onClick={nav('team_tasks')}>{t('Zadaci', 'Tasks')}</button>
                    </div>
                    {overdueCount > 0 && (
                        <div className="cc-overdue-badge">{overdueCount} {t('zakasnjelo', 'overdue')}</div>
                    )}
                    <div className="cc-task-list">
                        {taskList.map((task, index) => (
                            <button type="button" key={task.id} onClick={nav('team_tasks')}>
                                <em>{String(index + 1).padStart(2, '0')}</em>
                                <strong>{task.title}</strong>
                                <span>{task.due_date ? new Date(task.due_date).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' }) : t('bez roka', 'no due date')}</span>
                            </button>
                        ))}
                        {taskList.length === 0 && <p className="cc-empty">{t('Radni red je cist.', 'The work queue is clear.')}</p>}
                    </div>
                    {(overdueCount + todayCount) > 0 && (
                        <div className="cc-task-summary">
                            <span>{overdueCount} {t('zakasnjelo', 'overdue')}</span>
                            <span>{todayCount} {t('danas', 'today')}</span>
                            <span>{tasks?.upcoming?.length ?? 0} {t('dolazi', 'upcoming')}</span>
                        </div>
                    )}
                </article>

                {/* --- Pipeline / Sales funnel --- */}
                {kpi && (
                    <article className="cc-card cc-pipeline" onClick={nav('ado')} role="button" tabIndex={0}>
                        <div className="cc-card-head"><span>06</span><h3>{t('Prodajni cjevovod', 'Sales pipeline')}</h3></div>
                        <div className="cc-pipe-metrics">
                            <div>
                                <small>{t('OTVORENE PONUDE', 'OPEN OFFERS')}</small>
                                <strong>{kpi.offers_open_count}</strong>
                                <b>{compactEur(kpi.offers_open_amount ?? 0, locale)} EUR</b>
                            </div>
                            <div>
                                <small>{t('KONVERZIJA', 'CONVERSION')}</small>
                                <strong>{conversion.toFixed(1)}%</strong>
                                <i style={{ '--conv': `${Math.min(conversion, 100)}%` } as React.CSSProperties} />
                            </div>
                            <div>
                                <small>{t('RIZIK', 'RISK')}</small>
                                <strong className="is-warn">{compactEur(openRisk, locale)}</strong>
                                <b>{openRiskCount} {t('ponuda', 'offers')}</b>
                            </div>
                        </div>
                        {buybackTrend.length > 2 && (
                            <div className="cc-card-chart">
                                <span className="cc-chart-label">{t('OTKUP TREND', 'BUYBACK TREND')}</span>
                                <Spark values={buybackTrend} width={200} height={28} color="#6b6b63" />
                            </div>
                        )}
                        {(pulse?.oldest_open ?? []).length > 0 && (
                            <div className="cc-oldest">
                                <span className="cc-chart-label">{t('NAJSTARIJE OTVORENE', 'OLDEST OPEN')}</span>
                                {pulse!.oldest_open.slice(0, 3).map((o) => (
                                    <div key={o.offer_id} className="cc-oldest-row">
                                        <strong>{o.partner_name}</strong>
                                        <span>{compactEur(o.amount_eur, locale)} EUR</span>
                                        <em>{o.age_days}d</em>
                                    </div>
                                ))}
                            </div>
                        )}
                    </article>
                )}
            </div>

            {/* === SYSTEM NAVIGATION === */}
            <section className="cc-modules" id="cc-modules">
                <div>
                    <p className="cc-label">{t('SUSTAV', 'SYSTEM')}</p>
                    <h2>{t('Svi moduli. Jedan klik.', 'All modules. One click.')}</h2>
                </div>
                <div className="cc-module-grid">
                    {modules.map((entry) => (
                        <button type="button" key={entry.key} onClick={nav(entry.key)}>
                            <span>{String(entry.code).padStart(2, '0')}</span>
                            <strong>{locale === 'en' ? entry.labelEn : entry.labelHr}</strong>
                            <small>{locale === 'en' ? entry.leadEn : entry.leadHr}</small>
                        </button>
                    ))}
                </div>
            </section>
        </section>
    );
}
